<script setup lang="ts">
import { SortMode } from "@lxc/app-device-common";
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import dayjs from "dayjs";
import type { SearchMode } from "~/core/composables/useSearch";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import { DISTINCT_VALUES_KEY } from "~/core/constants/constants";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { formatIsoDate } from "~/core/utils/date";
import { useDeleteFirmware } from "~/modules/firmware/composables/useDeleteFirmware";
import { useFetchFirmwares } from "~/modules/firmware/composables/useFetchFirmwares";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { FirmwareColumns } from "~/modules/firmware/models/FirmwareColumns.enum";
import type { FilterOptions } from "~/types";
import { Filters } from "~/types";
import type { ColumnNameAndWidthI } from "~/types/table.type";
import { calculateTableMinWidth, getColumnWidth } from "~/utils/table.utils";

const DISTINCT_VALUE_DEVICE_TYPE_FRIENDLY_NAME = "data.deviceTypeFriendlyName";

const props = defineProps<{
  pathContext?: string;
  businessUnitCode?: number;
  organizationCode?: number;
  noAction?: boolean;
  selectable?: boolean;
  defaultFilters?: Map<Filters, string | string[]>;
  searchMode?: SearchMode;
  selectedFirmware?: Firmware | null;
  firmwareDetail?: Firmware;
  columns?: FirmwareColumns[];
  displayInfo?: boolean;
  statusOptions?: FilterOptions;
  rangeOptions?: FilterOptions;
  useQueryParametersForPagination?: boolean;
  canManageFirmware?: boolean;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { navigateToPath, onClose } = useSideNavigation();

const emit = defineEmits(["update:selectedFirmware", "openFirmwareDetail"]);

const {
  isLoading,
  results,
  filters,
  error,
  fetchData,
  setFilter,
  onSortChange,
  onSearch,
  search,
} = useFetchFirmwares({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
  searchMode: props.searchMode,
  useQueryParametersForPagination: props.useQueryParametersForPagination,
});
const { deleteFirmware } = useDeleteFirmware({
  businessUnitCode: props.businessUnitCode,
});

const tableRef = ref<InstanceType<typeof LxcTable>>();

const searchQuery = computed({
  get() {
    return (filters.get(Filters.FIRMWARE_NAME_OR_VERSION) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.FIRMWARE_NAME_OR_VERSION, searchQuery);
  },
});

const computedRangeOptions = computed(() => {
  if (
    results.value &&
    results.value.distinctValues &&
    results.value.distinctValues[DISTINCT_VALUE_DEVICE_TYPE_FRIENDLY_NAME]
  ) {
    return {
      options: results.value.distinctValues[
        DISTINCT_VALUE_DEVICE_TYPE_FRIENDLY_NAME
      ].map((value) => {
        return { label: value, value };
      }),
    };
  } else {
    return props.rangeOptions;
  }
});

const uuid = ref<string | undefined>();

/**
 * Retrieve selected firmware
 */
const setSelection = () => {
  if (tableRef.value && tableRef.value?.data) {
    const row = (tableRef.value.data as Firmware[]).find(
      (row: Firmware) => row.uuid === props.selectedFirmware?.uuid,
    );
    if (row) {
      tableRef.value.setCurrentRow(row);
    }
  }
};

watch(() => props.selectedFirmware, setSelection);

/**
 * Retrieve firmwares and set selected firmware if needed
 * @param page
 * @param pageSize
 */
async function loadData(page?: number, pageSize?: number) {
  const params = new Map();
  params.set(DISTINCT_VALUES_KEY, DISTINCT_VALUE_DEVICE_TYPE_FRIENDLY_NAME);
  await fetchData(page, pageSize, params);

  setTimeout(setSelection, 0);
}

const updateEditedFirmware = (paramFirmware: Firmware) => {
  if (results.value?.results) {
    const firmware = results.value.results.find(
      (currentFirmware: Firmware) =>
        currentFirmware.uuid === paramFirmware.uuid,
    );
    if (firmware) {
      firmware.status = paramFirmware.status;
    }
  }
};

function reloadWithContext() {
  loadData(
    results.value?.pagination?.page,
    results.value?.pagination?.pageSize,
  );
}

const computedSelectedFirmware = computed({
  get(): Firmware | undefined | null {
    return props.selectedFirmware;
  },
  set(selectedFirmware: Firmware | undefined | null) {
    emit("update:selectedFirmware", selectedFirmware);
  },
});

/**
 * Update the selected firmware or redirect to firmware details page
 */
async function onSelectFirmware(firmware: Firmware | undefined) {
  if (props.selectable) {
    computedSelectedFirmware.value = firmware;
  } else if (firmware) {
    uuid.value = firmware.uuid;

    if (firmware?.uuid) {
      await navigateToPath(`${props.pathContext}/${firmware?.uuid}`);
    }
  }
}

const onCloseFirmwareDetail = async (event: CloseEvent) => {
  if (props.pathContext) {
    await onClose(event, props.pathContext, reloadWithContext);
  }
};

const onDeleteFirmware = async () => {
  await router.push({
    path: props.pathContext,
    query: route.query,
  });
  reloadWithContext();
};

const initDisplayFromRoute = () => {
  if (props.pathContext && route.path.startsWith(props.pathContext)) {
    uuid.value = route?.params?.uuid as string | undefined;
  }
};

const columnsWidth: ComputedRef<ColumnNameAndWidthI<FirmwareColumns>[]> =
  computed(() => {
    const columnsNameAndWidth: ColumnNameAndWidthI<FirmwareColumns>[] = [
      {
        columnName: FirmwareColumns.NAME,
        width: 12,
      },
      {
        columnName: FirmwareColumns.RANGE,
        width: 8,
      },
      {
        columnName: FirmwareColumns.DECLINATION,
        width: 8,
      },
      {
        columnName: FirmwareColumns.VERSION,
        width: 12,
      },
      {
        columnName: FirmwareColumns.CREATION_DATE,
        width: 12,
      },
      {
        columnName: FirmwareColumns.STATUS,
        width: 8,
      },
      {
        columnName: FirmwareColumns.BUILD_DATE,
        width: 12,
      },
    ];

    if (!props.noAction) {
      columnsNameAndWidth.push({
        columnName: FirmwareColumns.ACTIONS,
        width: 12,
      });
    }

    return columnsNameAndWidth;
  });

const tableMinWidth: ComputedRef<string | undefined> = computed(() => {
  return calculateTableMinWidth<FirmwareColumns>(
    columnsWidth.value,
    props.columns,
  );
});

const getWidth = (columnName: FirmwareColumns): string | undefined => {
  return getColumnWidth<FirmwareColumns>(columnName, columnsWidth.value);
};

const onUnselectFirmware = () => {
  tableRef.value?.setCurrentRow(undefined);
  onSelectFirmware(undefined);
};

watch(route, initDisplayFromRoute);

onMounted(async () => {
  initDisplayFromRoute();

  await onSearch(loadData, false);

  // the default filters must be defined between onSearch and search execution in order to be taken into account in URL_SEARCH mode
  if (props.defaultFilters) {
    for (const [filter, value] of props.defaultFilters) {
      setFilter(filter, value ?? "");
    }
  }

  await search();
});

defineExpose({
  search,
});
</script>

<template>
  <search-bar
    v-model:search-query="searchQuery"
    :search-placeholder="t('firmware.filters.searchByNameOrVersion')"
    @clear="search"
    @search="search"
  />
  <firmware-filters
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
    :filters="filters"
    :status-options="statusOptions"
    :range-options="computedRangeOptions"
    @change="setFilter"
    @enter="search"
  />
  <div v-if="props.selectable" class="flex justify-items-center items-center">
    <div class="py-5 pr-2">{{ t("firmware.selected") }}</div>
    <lxc-tag
      v-if="computedSelectedFirmware"
      border-visible
      deletable
      @delete="onUnselectFirmware"
    >
      {{ computedSelectedFirmware.name }}
    </lxc-tag>
  </div>
  <lxc-table
    ref="tableRef"
    :min-width="tableMinWidth"
    class="mb-4"
    :data="results?.results"
    :context="results?.pagination"
    :is-loading="isLoading"
    :error="error?.toError()"
    clickable
    :empty-text="t('firmware.noFirmware')"
    @change-page-and-page-size="loadData"
    @row-click="onSelectFirmware"
    @sort-change="onSortChange"
  >
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.NAME)"
      :prop="FirmwareColumns.NAME"
      :label="t('firmware.column.versionName')"
      :sort-mode="SortMode.CUSTOM"
      :min-width="getWidth(FirmwareColumns.NAME)"
    />
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.RANGE)"
      :prop="FirmwareColumns.RANGE"
      :label="t('firmware.column.type')"
      :width="getWidth(FirmwareColumns.RANGE)"
    />
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.DECLINATION)"
      :prop="FirmwareColumns.DECLINATION"
      :label="t('firmware.column.declination')"
      :width="getWidth(FirmwareColumns.DECLINATION)"
    />
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.VERSION)"
      :prop="FirmwareColumns.VERSION"
      :label="t('firmware.column.version')"
      :sort-mode="SortMode.CUSTOM"
      :width="getWidth(FirmwareColumns.VERSION)"
    />
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.BUILD_DATE)"
      :prop="FirmwareColumns.BUILD_DATE"
      :label="t('firmware.column.buildDate')"
      :width="getWidth(FirmwareColumns.BUILD_DATE)"
    >
      <template #default="scope">
        {{
          scope.row.buildDate
            ? formatIsoDate(scope.row.buildDate, t("dateFormat.date"))
            : ""
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!columns || columns?.includes(FirmwareColumns.CREATION_DATE)"
      :prop="FirmwareColumns.CREATION_DATE"
      :label="t('firmware.column.creationDate')"
      :sort-mode="SortMode.CUSTOM"
      :width="getWidth(FirmwareColumns.CREATION_DATE)"
    >
      <template #default="scope">
        {{
          (scope.row as Firmware).creationDate
            ? dayjs((scope.row as Firmware).creationDate).format(
                t("firmware.column.dateFormat"),
              )
            : ""
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="
        businessUnitCode &&
        (!columns || columns?.includes(FirmwareColumns.STATUS))
      "
      :prop="FirmwareColumns.STATUS"
      :label="t('firmware.column.status')"
      class="!py-0"
      :width="getWidth(FirmwareColumns.STATUS)"
    >
      <template #default="scope">
        <firmware-status-badge :status="scope.row.status" />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!noAction"
      class="w-16 !py-0"
      :width="getWidth(FirmwareColumns.ACTIONS)"
    >
      <template #default="scoped">
        <div class="flex">
          <template v-if="canManageFirmware && businessUnitCode">
            <firmware-activate-action
              :business-unit-code="businessUnitCode"
              :firmware="scoped.row"
              @change="updateEditedFirmware"
            />
            <firmware-deactivate-action
              :business-unit-code="businessUnitCode"
              :firmware="scoped.row"
              @change="updateEditedFirmware"
            />
            <delete-action
              :delete-fn="() => deleteFirmware(scoped.row.uuid)"
              :title="t('firmware.actions.delete.label')"
              :confirm-title="t('firmware.actions.delete.label')"
              :confirm-description="
                t('firmware.actions.delete.description', {
                  name: scoped.row.name,
                })
              "
              @success="reloadWithContext"
            />
          </template>
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>

  <firmware-details
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
    :uuid="uuid"
    @close="onCloseFirmwareDetail"
    @delete="onDeleteFirmware"
  />

  <firmware-info
    v-if="displayInfo"
    v-model:firmware="computedSelectedFirmware"
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
  />
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
